import { Maybe } from 'maybeasy';
import { Link, Resource } from '../../../Resource/Types';

export type EffectiveTier = 'collection' | 'organization' | 'default';

// Themed sections of the communication list, in journey order. The server
// owns which trigger belongs to which group; 'other' is the catch-all for a
// trigger that has not been mapped yet.
export type CommunicationGroup =
  | 'program_invitations'
  | 'coaching_invitations'
  | 'enrollment_reminders'
  | 'getting_started'
  | 'group_coaching'
  | 'during_the_program'
  | 'completion'
  | 'account'
  | 'other';

export interface Override {
  id: number;
  subject: string;
  content: Maybe<string>;
  ctaLabel: Maybe<string>;
  ctaUrl: Maybe<string>;
  links: ReadonlyArray<Link>;
}

export interface CommunicationEntry {
  communicationId: number;
  name: string;
  publicName: Maybe<string>;
  trigger: Maybe<string>;
  group: CommunicationGroup;
  effectiveTier: EffectiveTier;
  // Switched off for this collection: participants here receive nothing for
  // this communication, whatever the inherited tiers say.
  disabled: boolean;
  // Whether the server will honour a disable at all. Transactional messages
  // and the opening message of an invitation ladder are withheld.
  disableable: boolean;
  disableRestriction: Maybe<string>;
  defaultSubject: string;
  organizationSubject: Maybe<string>;
  override: Maybe<Override>;
  links: ReadonlyArray<Link>;
}

// 'missing'          - the communication has no template at any tier
// 'unavailable'      - the inherited body could not be read
// 'not_extractable'  - the body was read but no editable copy was found
export type InheritedStatus = 'ok' | 'missing' | 'unavailable' | 'not_extractable';

export interface InheritedTemplatePayload {
  communicationId: number;
  tier: Maybe<string>;
  status: InheritedStatus;
  subject: Maybe<string>;
  content: Maybe<string>;
  ctaLabel: Maybe<string>;
  ctaUrl: Maybe<string>;
}

export type InheritedTemplateResource = Resource<InheritedTemplatePayload>;

// Which action is in flight. A global "busy" flag left the whole list inert
// with nothing moving, which reads as a frozen screen; naming the target lets
// the spinner appear on the row or section actually being changed.
export type PendingAction =
  | { kind: 'row'; communicationId: number }
  | { kind: 'section'; group: CommunicationGroup };

export interface CollectionSummary {
  id: number;
  name: string;
}

export interface OverridesIndexPayload {
  collection: CollectionSummary;
  communications: CommunicationEntry[];
}

export type OverridesIndexResource = Resource<OverridesIndexPayload>;

export interface SampleParticipant {
  firstName: string;
  lastName: string;
  email: string;
}

export interface PreviewPayload {
  subject: string;
  html: string;
  unreplacedTokens: string[];
  conditionalTokens: string[];
  sampleParticipant: SampleParticipant;
}

export type PreviewResource = Resource<PreviewPayload>;

export interface Draft {
  subject: string;
  content: string;
  ctaLabel: string;
  ctaUrl: string;
}

interface IndexLoading {
  kind: 'loading';
}

interface IndexErrored {
  kind: 'errored';
}

interface IndexReady {
  kind: 'ready';
  resource: OverridesIndexResource;
}

export type IndexState = IndexLoading | IndexErrored | IndexReady;

export const indexLoading = (): IndexState => ({ kind: 'loading' });
export const indexErrored = (): IndexState => ({ kind: 'errored' });
export const indexReady = (resource: OverridesIndexResource): IndexState => ({
  kind: 'ready',
  resource,
});

interface PreviewIdle {
  kind: 'idle';
}

interface PreviewLoading {
  kind: 'loading';
}

interface PreviewReady {
  kind: 'ready';
  preview: PreviewPayload;
}

interface PreviewErrored {
  kind: 'errored';
}

export type PreviewState = PreviewIdle | PreviewLoading | PreviewReady | PreviewErrored;

export const previewIdle = (): PreviewState => ({ kind: 'idle' });
export const previewLoading = (): PreviewState => ({ kind: 'loading' });
export const previewReady = (preview: PreviewPayload): PreviewState => ({
  kind: 'ready',
  preview,
});
export const previewErrored = (): PreviewState => ({ kind: 'errored' });

interface SeedIdle {
  kind: 'idle';
}

interface SeedLoading {
  kind: 'loading';
}

interface SeedApplied {
  kind: 'applied';
  tier: Maybe<string>;
}

interface SeedUnavailable {
  kind: 'unavailable';
  status: InheritedStatus;
}

export type SeedState = SeedIdle | SeedLoading | SeedApplied | SeedUnavailable;

export const seedIdle = (): SeedState => ({ kind: 'idle' });
export const seedLoading = (): SeedState => ({ kind: 'loading' });
export const seedApplied = (tier: Maybe<string>): SeedState => ({ kind: 'applied', tier });
export const seedUnavailable = (status: InheritedStatus): SeedState => ({
  kind: 'unavailable',
  status,
});

interface SaveIdle {
  kind: 'idle';
}

interface Saving {
  kind: 'saving';
}

interface Saved {
  kind: 'saved';
}

interface SaveErrored {
  kind: 'errored';
}

export type SaveState = SaveIdle | Saving | Saved | SaveErrored;

export const saveIdle = (): SaveState => ({ kind: 'idle' });
export const saving = (): SaveState => ({ kind: 'saving' });
export const saved = (): SaveState => ({ kind: 'saved' });
export const saveErrored = (): SaveState => ({ kind: 'errored' });
